import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, assessorGuess } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { composeAddress, emptyAddress } from "@/components/AddressFields";
import { todayLocal } from "@/lib/date";
import { parseDollarInput } from "@/hooks/useCentsField";
import { validateNumericField, validateYearField } from "@/lib/validateNumericField";
import { toast } from "sonner";

// ── OA-471 §5/T2 — shared 422 → inline field-error mapping ───────────────────
// FastAPI validation errors arrive as `{ detail: [{ loc, msg, type }] }`. Take
// the LAST element of `detail[0].loc` as the field name and `detail[0].msg` as
// the message (`["body", "foundation_type"]` → `foundation_type`). A bare-string
// `detail` (business-rule 422 with no `loc`, e.g. "asset already has a tax
// assessment for this year") renders under the field/row currently being saved.
// Absent/malformed `detail` (non-FastAPI 422, or a proxy error wearing a 422)
// returns `null` so the caller degrades to the legacy toast
// (`e?.response?.data?.detail || "Save failed"`) instead of crashing on an
// undefined read — it must never throw. Written ONCE here and reused by the
// field-save wrapper below and (T3/T4) every history-row add form — never
// hand-rolled per field.
export function map422Error(fieldName, error) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (first && typeof first === "object") {
      const loc = Array.isArray(first.loc) ? first.loc : [];
      const field = loc.length ? loc[loc.length - 1] : fieldName;
      return { field: String(field), message: first.msg || "Invalid value" };
    }
    return null;
  }
  if (typeof detail === "string") {
    return { field: fieldName, message: detail };
  }
  return null; // absent / malformed → caller falls back to the legacy toast
}

// ── OA-471 §1/T3 — per-field parse/validate for the new scalar fields ────────
// Money fields convert dollars→cents via useCentsField's decimal-string parser
// (rejects >2 decimals, commas, garbage, negatives — never silently rounds).
// Numeric fields validate against the REAL backend bounds (ourhomebase-api
// `family.py`: `StrictYear` = 1950..current_year+1, computed dynamically;
// `depreciation_useful_life_years` = Field(ge=1, le=100); sqft/acres = Field(ge=0),
// no hard upper bound). Blank means "no change, don't submit this field" — never
// an implicit 0 or clear (clearing goes through the explicit `clear_fields` UI).
// Returns `{ skip }` (blank), `{ error }` (client-side reject, no network call),
// or `{ value }` (parsed payload value ready to PUT).
const NEW_MONEY_FIELDS = new Set(["depreciable_basis", "last_filing_accumulated_depreciation"]);
const NEW_YEAR_FIELDS = new Set([
  "year_built", "roof_install_year", "hvac_install_year", "plumbing_update_year",
  "electrical_update_year", "water_heater_install_year", "last_filing_tax_year",
]);
const NEW_NONNEG_FIELDS = new Set(["above_grade_sqft", "finished_basement_sqft", "lot_size_acres"]);
const FOUNDATION_TYPES = new Set([
  "poured_concrete", "block", "slab", "crawlspace", "basement", "stone", "pier_and_beam", "other",
]);

function parseScalarField(fieldName, rawValue) {
  if (NEW_MONEY_FIELDS.has(fieldName)) {
    const { cents, error, isBlank } = parseDollarInput(rawValue);
    if (isBlank) return { skip: true };
    if (error) return { error };
    return { value: cents };
  }

  const isBlank = rawValue == null || String(rawValue).trim() === "";
  if (isBlank) return { skip: true };

  if (NEW_YEAR_FIELDS.has(fieldName)) {
    const { value, error } = validateYearField(rawValue);
    return error ? { error } : { value };
  }

  if (fieldName === "depreciation_useful_life_years") {
    const { value, error } = validateNumericField(rawValue, {
      min: 1,
      max: 100,
      message: "Enter a number of years between 1 and 100.",
    });
    return error ? { error } : { value };
  }

  if (NEW_NONNEG_FIELDS.has(fieldName)) {
    const { value, error } = validateNumericField(rawValue, {
      min: 0,
      message: "Enter a non-negative number.",
    });
    return error ? { error } : { value };
  }

  if (fieldName === "foundation_type") {
    const v = String(rawValue).trim();
    if (!FOUNDATION_TYPES.has(v)) return { error: "Select a valid foundation type." };
    return { value: v };
  }

  // placed_in_service_date, parcel_number, and any other string field: pass the
  // trimmed raw value through (blank is already handled above as "no change").
  return { value: String(rawValue).trim() };
}

/**
 * Data layer for the AssetDetail page. Owns all state, effects and API calls,
 * and returns the data, loading state and action callbacks. Pure extraction of
 * what used to live inline in AssetDetail — no behavior change.
 */
export function useAssetDetail(id, hid) {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAssets, refreshLiabilities } = useHousehold();

  const [asset, setAsset] = useState(null);
  const [taxRows, setTaxRows] = useState([]);
  const [assessorUrl, setAssessorUrl] = useState("");
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveFiles, setArchiveFiles] = useState([]);
  const [insUrl, setInsUrl] = useState("");
  const [editValue, setEditValue] = useState(null);   // string while editing the value
  const [editAddr, setEditAddr] = useState(null);      // object while editing the address
  const [op, setOp] = useState(null);                  // operating data (units / income / P&L)
  const [unitOpen, setUnitOpen] = useState(false);
  const [unit, setUnit] = useState({ label: "", monthly_rent: "", status: "occupied", mgmt_fee: "", mgmt_fee_pct: "" });
  const [activeUnit, setActiveUnit] = useState(null);  // unit shown in the detail popup
  const [leases, setLeases] = useState([]);
  const [pms, setPms] = useState([]);
  const leaseRef = useRef();
  const [leaseBusy, setLeaseBusy] = useState(false);
  const [linkLoanOpen, setLinkLoanOpen] = useState(false);
  const [primaryDocUploading, setPrimaryDocUploading] = useState(false);
  const fileRefs = { purchase_contract: useRef(), title_insurance: useRef(), photo: useRef() };

  // OA-471 §3 — tax-assessments collection (add-form + per-row error state). The
  // hook owns add/delete state and mutation actions; AssetDetail stays presentational.
  const [taxAssessmentError, setTaxAssessmentError] = useState(null);      // add-form inline error
  const [taxAssessmentRowErrors, setTaxAssessmentRowErrors] = useState({}); // per-existing-row error, keyed by server id
  const addTaxRef = useRef(false);            // synchronous double-click guard for the single add form
  const deleteTaxRef = useRef(new Set());     // synchronous double-click guard, keyed by row id

  const isRE = asset?.asset_type === "real_estate";

  const loadOperating = useCallback(async () => {
    try { const r = await api.get(`/assets/${id}/operating?household_id=${hid}`); setOp(r.data); }
    catch (e) { /* operating data optional */ }
    try { const p = await api.get(`/assets/${id}/property-managers?household_id=${hid}`); setPms(p.data); }
    catch (e) { /* pm list optional */ }
  }, [hid, id]);

  const assignPM = async (val) => {
    try {
      await api.put(`/assets/${id}/property-manager?household_id=${hid}`, { property_manager_id: val === "none" ? null : val });
      toast.success(val === "none" ? "Manager unlinked" : "Property manager linked");
      loadOperating();
    } catch (e) { toast.error("Failed to update manager"); }
  };

  const load = useCallback(async () => {
    if (!hid || !id) return;
    try {
      const r = await api.get(`/assets/${id}?household_id=${hid}`);
      const a = r.data;
      // Real estate lives under the Properties silo — keep the URL canonical.
      if (a.asset_type === "real_estate" && location.pathname.startsWith("/assets/")) {
        navigate(`/properties/${id}`, { replace: true });
        return;
      }
      setAsset(a);
      setTaxRows(a.tax_records?.length ? a.tax_records : defaultTaxRows());
      setAssessorUrl(a.assessor_url || assessorGuess(a.address));
      if (a.asset_type === "real_estate") {
        loadOperating();
        if (a.address && !a.geo) geocode(a.address);
      }
    } catch (e) { toast.error("Asset not found"); navigate("/assets"); }
  }, [hid, id, navigate, location.pathname, loadOperating]);
  useEffect(() => { load(); }, [load]);

  const addUnit = async () => {
    if (!unit.label.trim()) { toast.error("Unit label is required"); return; }
    try {
      await api.post(`/assets/${id}/units?household_id=${hid}`, {
        label: unit.label, monthly_rent: unit.monthly_rent === "" ? 0 : Number(unit.monthly_rent),
        status: unit.status,
        mgmt_fee: unit.mgmt_fee === "" ? 0 : Number(unit.mgmt_fee),
        mgmt_fee_pct: unit.mgmt_fee_pct === "" ? null : Number(unit.mgmt_fee_pct),
      });
      setUnitOpen(false); setUnit({ label: "", monthly_rent: "", status: "occupied", mgmt_fee: "", mgmt_fee_pct: "" });
      loadOperating(); toast.success("Unit added");
    } catch (e) { toast.error("Failed to add unit"); }
  };
  const removeUnit = async (uid) => {
    if (!window.confirm("Remove this unit?")) return;
    await api.delete(`/assets/${id}/units/${uid}?household_id=${hid}`); loadOperating();
  };

  const openUnit = async (u) => {
    setActiveUnit(u); setLeases([]);
    try { const r = await api.get(`/assets/${id}/units/${u.id}/leases?household_id=${hid}`); setLeases(r.data); } catch (e) {}
  };
  const refreshLeases = async (uid) => {
    try { const r = await api.get(`/assets/${id}/units/${uid}/leases?household_id=${hid}`); setLeases(r.data); } catch (e) {}
  };
  const uploadLease = async (file) => {
    if (!file || !activeUnit) return;
    setLeaseBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/assets/${id}/units/${activeUnit.id}/leases?household_id=${hid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (leaseRef.current) leaseRef.current.value = "";
      toast.success("Lease added — AI extracted the terms");
      refreshLeases(activeUnit.id); loadOperating();
    } catch (e) { toast.error("Lease upload failed"); }
    finally { setLeaseBusy(false); }
  };
  const addBlankLease = async () => {
    if (!activeUnit) return;
    try {
      await api.post(`/assets/${id}/units/${activeUnit.id}/leases?household_id=${hid}`, new FormData());
      refreshLeases(activeUnit.id); loadOperating();
    } catch (e) { toast.error("Could not add lease"); }
  };
  const updateLease = async (lid, patch) => {
    await api.put(`/assets/${id}/units/${activeUnit.id}/leases/${lid}?household_id=${hid}`, patch);
    refreshLeases(activeUnit.id); loadOperating();
  };
  const deleteLease = async (lid) => {
    if (!window.confirm("Delete this lease?")) return;
    await api.delete(`/assets/${id}/units/${activeUnit.id}/leases/${lid}?household_id=${hid}`);
    refreshLeases(activeUnit.id); loadOperating();
  };
  const viewLease = async (lid) => {
    try {
      const r = await api.get(`/assets/${id}/units/${activeUnit.id}/leases/${lid}/file?household_id=${hid}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("No lease document"); }
  };

  const defaultTaxRows = () => {
    const y = new Date().getFullYear() - 1;
    return [0, 1, 2].map((i) => ({ year: y - i, amount_paid: "", status: "paid" }));
  };

  const stripLease = (lz) => ({
    tenant_name: lz.tenant_name || "", start_date: lz.start_date || null, end_date: lz.end_date || null,
    monthly_rent: lz.monthly_rent ?? null, security_deposit: lz.security_deposit ?? null, terms_summary: lz.terms_summary || "",
  });

  const geocode = async (addressArg) => {
    const address = addressArg || asset?.address || "";
    if (!address) return;
    try {
      const resp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`, {
        headers: { "Accept": "application/json" },
      });
      const rows = await resp.json();
      if (!rows?.length) { toast.error("Couldn't locate that address on the map"); return; }
      const geo = { lat: parseFloat(rows[0].lat), lon: parseFloat(rows[0].lon), display_name: rows[0].display_name };
      const updated = await saveDetails({ geo }, "");
      if (updated) setAsset(updated);
    } catch (e) { toast.error("Map lookup failed — try again"); }
  };

  const saveDetails = async (patch, msg = "Saved") => {
    try {
      const r = await api.put(`/assets/${id}/details?household_id=${hid}`, patch);
      setAsset(r.data); refreshAssets();
      if (msg) toast.success(msg);
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveValue = async () => {
    const v = editValue === "" ? 0 : Number(editValue);
    if (Number.isNaN(v)) { toast.error("Enter a valid amount"); return; }
    await saveDetails({ current_value: v, value_source: "manual", value_as_of: todayLocal() }, "Value updated");
    setEditValue(null);
  };

  // OA-471 §3/§4 — narrow computed-values refresh. `PUT /assets/{id}/details`
  // returns the raw asset with NO `computed` block (only GET attaches it), so
  // after any computed-feeding mutation we re-fetch and apply ONLY the fields
  // relevant to computed values plus the mutated collection's own array.
  // Deliberately NARROWER than load(): it never calls setTaxRows(), so a user's
  // unsaved edits in the legacy tax table survive. Best-effort — if this GET
  // fails, the already-succeeded row/scalar change still stands.
  const refreshComputed = useCallback(async () => {
    if (!hid || !id) return;
    try {
      const r = await api.get(`/assets/${id}?household_id=${hid}`);
      const fresh = r.data || {};
      setAsset((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        if (fresh.computed !== undefined) next.computed = fresh.computed;
        if (fresh.property_tax_assessments !== undefined) next.property_tax_assessments = fresh.property_tax_assessments;
        return next;
      });
    } catch (e) { /* best-effort; the row/scalar change already succeeded */ }
  }, [hid, id]);

  // OA-471 §5/T2 — field-save wrapper for the 16 new scalar fields. This is a
  // NEW code path alongside the legacy saveValue()/saveDetails() flow, NOT a
  // retrofit of it: the legacy path toasts+swallows a failure and closes edit
  // mode unconditionally; this one surfaces a 422 inline so the field can stay
  // open. It only touches the new fields — current_value/tax_records keep their
  // existing behavior untouched.
  //   - client-side reject (garbage / out-of-bounds) → { error } with NO network call
  //   - blank                                        → no-op (no change, not submitted)
  //   - success                                      → setAsset(r.data) + refreshAssets()
  //                                                     (success-path parity with saveDetails callers)
  //   - 422 with usable detail                       → { error: { field, message } } (caller renders inline)
  //   - 422 w/o detail, or network/other error       → legacy toast, degrades gracefully, never throws
  const saveScalarField = async (fieldName, rawValue) => {
    const parsed = parseScalarField(fieldName, rawValue);
    if (parsed.skip) return undefined;             // blank = no change, don't submit
    if (parsed.error) return { error: { field: fieldName, message: parsed.error } };
    try {
      const r = await api.put(`/assets/${id}/details?household_id=${hid}`, { [fieldName]: parsed.value });
      setAsset(r.data);
      refreshAssets();
      // Every Round 1 depreciation anchor feeds a §4 computed value; the PUT
      // response carries no `computed` block, so re-fetch it narrowly.
      await refreshComputed();
      return { data: r.data };
    } catch (e) {
      if (e?.response?.status === 422) {
        const mapped = map422Error(fieldName, e);
        if (mapped) return { error: mapped };
      }
      // 422 without a usable detail, or any non-422 error: fall back to the
      // pre-existing generic toast pattern rather than crashing on an undefined read.
      toast.error(e?.response?.data?.detail || "Save failed");
      return undefined;
    }
  };

  // OA-471 §2 — clear a scalar field to "unknown". Sends exactly
  // `{ clear_fields: [fieldName] }` (never a value alongside it). Same 422/inline
  // contract as saveScalarField, and the same narrow computed refresh on success.
  const clearScalarField = async (fieldName) => {
    try {
      const r = await api.put(`/assets/${id}/details?household_id=${hid}`, { clear_fields: [fieldName] });
      setAsset(r.data);
      refreshAssets();
      await refreshComputed();
      return { data: r.data };
    } catch (e) {
      if (e?.response?.status === 422) {
        const mapped = map422Error(fieldName, e);
        if (mapped) return { error: mapped };
      }
      toast.error(e?.response?.data?.detail || "Save failed");
      return undefined;
    }
  };

  // OA-471 §3 — tax-assessments collection: sorted year-descending view (the
  // array is NOT assumed pre-sorted), plus the always-present-with-nulls computed
  // block. Both derived from `asset`; the mutation actions below own the state.
  const taxAssessments = [...(asset?.property_tax_assessments || [])].sort(
    (a, b) => (Number(b.year) || 0) - (Number(a.year) || 0)
  );
  const computed = asset?.computed || null;

  // POST /assets/{id}/tax-assessments?household_id={hid}. `year` is required
  // (StrictYear bounds, client-blocked if blank/out-of-range). `assessed_value`
  // is money: a blank value OMITS the key entirely (never "" / 0). On success the
  // server-confirmed row (with its generated id) is appended directly — no second
  // GET for the row itself — then the narrow computed refresh fires. A failed POST
  // shows an inline add-form error and does NOT optimistically append. A duplicate
  // call while one is in flight is a no-op (synchronous ref guard).
  const addTaxAssessment = async ({ year, assessed_value }) => {
    if (addTaxRef.current) return undefined; // double-click guard (must be synchronous, pre-await)

    const yv = validateYearField(year);
    if (yv.isBlank) {
      const msg = "Year is required.";
      setTaxAssessmentError(msg);
      return { error: { field: "year", message: msg } };
    }
    if (yv.error) {
      setTaxAssessmentError(yv.error);
      return { error: { field: "year", message: yv.error } };
    }

    const body = { year: yv.value };
    const av = parseDollarInput(assessed_value);
    if (!av.isBlank) {
      if (av.error) {
        setTaxAssessmentError(av.error);
        return { error: { field: "assessed_value", message: av.error } };
      }
      body.assessed_value = av.cents;
    }

    addTaxRef.current = true;
    setTaxAssessmentError(null);
    try {
      const r = await api.post(`/assets/${id}/tax-assessments?household_id=${hid}`, body);
      const row = r.data;
      setAsset((prev) => (prev
        ? { ...prev, property_tax_assessments: [...(prev.property_tax_assessments || []), row] }
        : prev));
      await refreshComputed();
      return { data: row };
    } catch (e) {
      const mapped = e?.response?.status === 422 ? map422Error("assessed_value", e) : null;
      const msg = mapped ? mapped.message : (e?.response?.data?.detail || "Could not add assessment");
      setTaxAssessmentError(typeof msg === "string" ? msg : "Could not add assessment");
      return { error: mapped || { field: "assessed_value", message: "Could not add assessment" } };
    } finally {
      addTaxRef.current = false;
    }
  };

  // DELETE /assets/{id}/tax-assessments/{rowId}?household_id={hid}. On success the
  // row is removed from local state (no second GET) and computed refreshes. On
  // failure the row STAYS with an inline error — never optimistically removed.
  // Error/pending state is keyed by the server-generated row id (survives sorting).
  // A duplicate call for the same row id while one is in flight is a no-op.
  const deleteTaxAssessment = async (rowId) => {
    if (deleteTaxRef.current.has(rowId)) return undefined; // double-click guard (synchronous)
    deleteTaxRef.current.add(rowId);
    try {
      await api.delete(`/assets/${id}/tax-assessments/${rowId}?household_id=${hid}`);
      setAsset((prev) => (prev
        ? { ...prev, property_tax_assessments: (prev.property_tax_assessments || []).filter((r) => r.id !== rowId) }
        : prev));
      setTaxAssessmentRowErrors((errs) => { const n = { ...errs }; delete n[rowId]; return n; });
      await refreshComputed();
      return { data: { ok: true } };
    } catch (e) {
      setTaxAssessmentRowErrors((errs) => ({ ...errs, [rowId]: "Could not delete this row." }));
      return undefined;
    } finally {
      deleteTaxRef.current.delete(rowId);
    }
  };

  const saveOwnership = async (patch) => {
    try {
      const r = await api.put(`/assets/${id}/ownership?household_id=${hid}`, patch);
      setAsset(r.data); refreshAssets();
      toast.success("Ownership updated");
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveAddress = async () => {
    const parts = editAddr || emptyAddress;
    const addr = composeAddress(parts);
    const updated = await saveDetails({ address: addr, address_parts: parts }, "Address updated");
    setEditAddr(null);
    if (updated && isRE && addr) geocode(addr);  // re-pin the map to the new address
  };

  const saveTaxes = () => {
    const cleaned = taxRows
      .filter((t) => t.year)
      .map((t) => ({ year: Number(t.year), amount_paid: t.amount_paid === "" ? 0 : Number(t.amount_paid), status: t.status }));
    saveDetails({ tax_records: cleaned }, "Tax history saved");
  };
  const setRow = (i, key, val) => setTaxRows((rows) => rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addRow = () => setTaxRows((rows) => [...rows, { year: "", amount_paid: "", status: "paid" }]);
  const removeRow = (i) => setTaxRows((rows) => rows.filter((_, idx) => idx !== i));

  const uploadDoc = async (slot, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/assets/${id}/documents/${slot}?household_id=${hid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); if (fileRefs[slot].current) fileRefs[slot].current.value = ""; load();
    } catch (e) { toast.error("Upload failed"); }
  };
  const viewDoc = async (slot) => {
    try {
      const r = await api.get(`/assets/${id}/documents/${slot}?household_id=${hid}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("No document on file"); }
  };
  const deleteDoc = async (slot) => {
    if (!window.confirm("Remove this document?")) return;
    await api.delete(`/assets/${id}/documents/${slot}?household_id=${hid}`); toast.success("Removed"); load();
  };

  const uploadPhoto = async (files) => {
    // Accept a single File OR a FileList/array — uploads each in parallel.
    const list = !files ? [] : (files.length != null ? Array.from(files) : [files]);
    if (list.length === 0) return;
    const results = await Promise.allSettled(list.map((f) => {
      const fd = new FormData(); fd.append("file", f);
      return api.post(`/assets/${id}/photos?household_id=${hid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }));
    if (fileRefs.photo.current) fileRefs.photo.current.value = "";
    let ok = 0, dup = 0, failed = 0;
    for (const r of results) {
      if (r.status === "fulfilled") ok += 1;
      else if (r.reason?.response?.status === 409) dup += 1;
      else failed += 1;
    }
    const parts = [];
    if (ok) parts.push(`Uploaded ${ok}`);
    if (dup) parts.push(`skipped ${dup} duplicate${dup === 1 ? "" : "s"}`);
    if (failed) parts.push(`failed ${failed}`);
    const msg = parts.join(", ") || "No files";
    if (failed && !ok) toast.error(msg);
    else if (dup && !ok) toast.warning(msg);
    else if (failed || dup) toast.warning(msg);
    else toast.success(msg);
    load();
  };
  const deletePhoto = async (pid) => {
    await api.delete(`/assets/${id}/photos/${pid}?household_id=${hid}`); load();
  };

  const openChild = (url) => window.open(url, "_blank", "noopener,noreferrer,width=1100,height=800,menubar=no,toolbar=no,location=no");

  const openAssessor = () => {
    if (!assessorUrl) { toast.error("No assessor link yet"); return; }
    openChild(assessorUrl);
    setPasteUrl(asset?.assessor_url || "");
    setVerifyOpen(true);
  };
  const saveVerified = async (url) => {
    const finalUrl = (url ?? assessorUrl).trim();
    if (!finalUrl) { toast.error("Enter the assessor page URL"); return; }
    await saveDetails({ assessor_url: finalUrl, assessor_url_verified: true }, "Assessor link bookmarked");
    setAssessorUrl(finalUrl); setVerifyOpen(false);
  };

  const loadArchive = async () => {
    try { const r = await api.get("/archive/files"); setArchiveFiles(r.data); } catch (e) { setArchiveFiles([]); }
    setArchiveOpen(true);
  };
  const linkArchiveDoc = (b) => {
    saveDetails({ insurance_declaration: { type: "archive", ref: b.id, label: b.vendor || b.filename || "Document" } }, "Insurance declaration linked");
    setArchiveOpen(false);
  };
  const linkInsuranceUrl = () => {
    if (!insUrl.trim()) { toast.error("Enter a URL"); return; }
    saveDetails({ insurance_declaration: { type: "url", ref: insUrl.trim(), label: insUrl.trim() } }, "Insurance link saved");
    setInsUrl("");
  };
  const clearInsurance = () => saveDetails({ insurance_declaration: {} }, "Insurance reference cleared");

  const _buildLiabilityPayload = (loan, overrides = {}) => ({
    name: loan.name, liability_type: loan.liability_type,
    asset_id: loan.asset_id || null, entity_id: loan.entity_id || null,
    owner_party: loan.owner_party || "joint", lender: loan.lender || "",
    original_balance: loan.original_balance ?? 0, current_balance: loan.current_balance ?? 0,
    interest_rate: loan.interest_rate ?? null, monthly_payment: loan.monthly_payment ?? null,
    due_date: loan.due_date || null, notes: loan.notes || "",
    ...overrides,
  });

  const linkLoan = async (loan) => {
    try {
      await api.put(`/liabilities/${loan.id}?household_id=${hid}`, _buildLiabilityPayload(loan, { asset_id: id }));
      toast.success(`${loan.name} linked to this property`);
      setLinkLoanOpen(false);
      refreshLiabilities();
    } catch (e) { toast.error("Failed to link loan"); }
  };

  const unlinkLoan = async (loan) => {
    if (!window.confirm(`Unlink "${loan.name}" from this property?`)) return;
    try {
      await api.put(`/liabilities/${loan.id}?household_id=${hid}`, _buildLiabilityPayload(loan, { asset_id: null }));
      toast.success(`${loan.name} unlinked`);
      refreshLiabilities();
    } catch (e) { toast.error("Failed to unlink loan"); }
  };

  const uploadPrimaryDoc = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files are accepted"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setPrimaryDocUploading(true);
    try {
      await api.post(`/assets/${id}/primary-doc?household_id=${hid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Primary document uploaded");
      refreshAssets(); load();
    } catch (e) { toast.error("Upload failed"); }
    finally { setPrimaryDocUploading(false); }
  };

  const deletePrimaryDoc = async () => {
    if (!window.confirm("Remove primary document and its thumbnail?")) return;
    try {
      await api.delete(`/assets/${id}/primary-doc?household_id=${hid}`);
      toast.success("Primary document removed");
      refreshAssets(); load();
    } catch (e) { toast.error("Failed to remove document"); }
  };
  const openInsurance = async () => {
    const decl = asset?.insurance_declaration;
    if (!decl?.ref) return;
    if (decl.type === "url") { openChild(decl.ref); return; }
    try {
      const path = decl.type === "ingest" ? `/ingest/${decl.ref}/file?household_id=${hid}` : `/bills/${decl.ref}/pdf`;
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Linked document not found"); }
  };

  return {
    // data + loading
    asset, isRE, op, taxRows, assessorUrl, archiveFiles, leases, pms,
    // OA-471 Round 1 — computed values + tax-assessments collection
    computed, taxAssessments, taxAssessmentError, taxAssessmentRowErrors,
    // local UI state + setters
    verifyOpen, setVerifyOpen, pasteUrl, setPasteUrl,
    archiveOpen, setArchiveOpen, insUrl, setInsUrl,
    editValue, setEditValue, editAddr, setEditAddr,
    unitOpen, setUnitOpen, unit, setUnit, activeUnit, setActiveUnit,
    leaseBusy, linkLoanOpen, setLinkLoanOpen, primaryDocUploading,
    leaseRef, fileRefs,
    // actions
    assignPM, load, addUnit, removeUnit, openUnit, uploadLease, addBlankLease,
    updateLease, deleteLease, viewLease, stripLease, geocode,
    saveValue, saveScalarField, clearScalarField, refreshComputed,
    addTaxAssessment, deleteTaxAssessment,
    saveAddress, saveOwnership, saveTaxes, setRow, addRow, removeRow,
    uploadDoc, viewDoc, deleteDoc, uploadPhoto, deletePhoto, openChild,
    openAssessor, saveVerified, loadArchive, linkArchiveDoc, linkInsuranceUrl,
    clearInsurance, linkLoan, unlinkLoan, uploadPrimaryDoc, deletePrimaryDoc, openInsurance,
  };
}
